import { useProdutosPublico } from '@/hooks/useProdutos';
import { HeroSection } from '@/components/loja/HeroSection';
import { CategoriesSection } from '@/components/loja/CategoriesSection';
import { ProductCard } from '@/components/loja/ProductCard';
import { HowItWorksSection } from '@/components/loja/HowItWorksSection';
import { TestimonialsSection } from '@/components/loja/TestimonialsSection';
import { NewsletterSection } from '@/components/loja/NewsletterSection';
import { Button } from '@/components/ui/Button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export function Home() {
  const { data: produtos, isLoading } = useProdutosPublico();
  const produtosDestaque = produtos?.slice(0, 8);

  return (
    <div className="bg-gray-950">
      {/* Hero Section com 3D */}
      <HeroSection />

      {/* Categories Section */}
      <CategoriesSection />

      {/* Featured Products */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
          >
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                Mais Vendidos
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                Produtos em{' '}
                <span className="bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
                  Destaque
                </span>
              </h2>
            </div>
            <Link to="/catalogo">
              <Button
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white hover:text-gray-900 group"
                rightIcon={<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
              >
                Ver Todos
              </Button>
            </Link>
          </motion.div>

          {/* Products Grid */}
          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-gray-800 rounded-3xl mb-4" />
                  <div className="h-4 bg-gray-800 rounded w-3/4 mb-2" />
                  <div className="h-6 bg-gray-800 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {produtosDestaque?.map((produto: any, index: number) => (
                <ProductCard key={produto.id} produto={produto} index={index} />
              ))}
            </div>
          )}

          {/* View All Button Mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-12 text-center md:hidden"
          >
            <Link to="/catalogo">
              <Button
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white hover:text-gray-900"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Ver Todos os Produtos
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <HowItWorksSection />

      {/* Testimonials */}
      <TestimonialsSection />

      {/* About Section */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[100px] -translate-y-1/2" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-4">
                Sobre a Fort3D
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                A casa do maker que{' '}
                <span className="bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
                  cria e inova
                </span>{' '}
                todos os dias
              </h2>
              <div className="space-y-4 text-gray-400 text-lg leading-relaxed">
                <p>
                  A Fort3D nasceu da paixão por tecnologia e da vontade de tornar a impressão 3D 
                  acessível a todos. Somos uma equipe de makers, engenheiros e designers 
                  trabalhando juntos para entregar produtos de alta qualidade.
                </p>
                <p>
                  Nossa missão é transformar suas ideias em realidade, oferecendo não apenas 
                  produtos impressos, mas uma experiência completa desde o primeiro contato 
                  até a entrega final.
                </p>
              </div>
              
              {/* Stats */}
              <div className="mt-8 flex flex-wrap gap-8">
                {[
                  { value: '+5', label: 'Anos no mercado' },
                  { value: '+15k', label: 'Produtos entregues' },
                  { value: '99%', label: 'Clientes satisfeitos' },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  >
                    <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-gray-500 text-sm">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image/Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden">
                <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl flex items-center justify-center border border-white/5">
                  <div className="text-center p-12">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="w-48 h-48 mx-auto mb-8 rounded-full bg-gradient-to-br from-orange-500/20 via-pink-500/20 to-cyan-500/20 flex items-center justify-center"
                    >
                      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500/30 via-pink-500/30 to-cyan-500/30 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-cyan-500 flex items-center justify-center">
                          <span className="text-white font-bold text-2xl">F3D</span>
                        </div>
                      </div>
                    </motion.div>
                    <h3 className="text-2xl font-bold text-white mb-2">Fort3D Indústria</h3>
                    <p className="text-gray-400">Excelência em impressão 3D</p>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 bg-gray-800 rounded-2xl p-4 border border-white/10 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Qualidade Garantida</p>
                    <p className="text-gray-400 text-xs">Inspeção rigorosa</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-orange-500/10 to-cyan-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Tem um projeto em mente?
            </h2>
            <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto mb-10">
              Entre em contato conosco para orçamentos personalizados. 
              Fazemos protótipos, peças sob demanda e produção em escala.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/5511999999999" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button
                  variant="nuvem"
                  size="xl"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 border-0"
                >
                  Falar no WhatsApp
                </Button>
              </a>
              <Link to="/catalogo">
                <Button
                  variant="outline"
                  size="xl"
                  className="border-white/20 text-white hover:bg-white hover:text-gray-900"
                >
                  Explorar Produtos
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
